/**
 * Karved Concepts — Scroll Configurator
 * Handles state, scroll reveals, step interactions, and cart.
 */

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

var STATE_KEY = 'kc_configurator';

var defaultState = {
  event: null, brand: null, tier: null, template: null,
  cup: null, variantId: null, color: null, price: 0,
  quantity: 1, uploadUrl: null, step: 0
};

function loadState() {
  try {
    var raw = sessionStorage.getItem(STATE_KEY);
    return raw ? Object.assign({}, defaultState, JSON.parse(raw)) : Object.assign({}, defaultState);
  } catch (_) { return Object.assign({}, defaultState); }
}

function saveState(s) {
  try { sessionStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch (_) {}
}

var state = loadState();
var configData = null;
var productCache = {};

// ---------------------------------------------------------------------------
// Config data
// ---------------------------------------------------------------------------

function fetchConfigData() {
  if (configData) return Promise.resolve(configData);
  // The data file is a theme asset; its CDN URL comes from Liquid
  // (asset_url) on the event step. A 404 resolves rather than rejects,
  // so check r.ok before using the body.
  var holder = document.querySelector('[data-config-url]');
  var url = holder && holder.getAttribute('data-config-url');
  var empty = { events: [], brands: [], pricing_tiers: [], fees: {} };
  if (!url) { configData = empty; return Promise.resolve(configData); }
  return fetch(url)
    .then(function(r) { if (!r.ok) throw new Error('fetch failed'); return r.json(); })
    .then(function(d) { configData = d; return d; })
    .catch(function() { configData = empty; return configData; });
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

function calculatePrice(basePrice, qty) {
  if (!configData || !configData.pricing_tiers) return basePrice;
  var discount = 0;
  for (var i = 0; i < configData.pricing_tiers.length; i++) {
    var t = configData.pricing_tiers[i];
    if (qty >= t.min && (t.max === null || qty <= t.max)) { discount = t.discount || 0; break; }
  }
  return basePrice * (1 - discount);
}

function setupFee(qty) {
  if (!configData || !configData.fees) return 0;
  var f = configData.fees;
  if (qty >= (f.setup_fee_threshold_waived || 500)) return 0;
  if (qty >= (f.setup_fee_threshold_halved || 250)) return (f.setup_fee || 15) / 2;
  return f.setup_fee || 15;
}

// ---------------------------------------------------------------------------
// DOM helpers — safe, no innerHTML for untrusted content
// ---------------------------------------------------------------------------

function el(tag, attrs, children) {
  var node = document.createElement(tag);
  if (attrs) {
    Object.keys(attrs).forEach(function(k) {
      var v = attrs[k];
      if (k === 'class') node.className = v;
      else if (k === 'dataset') Object.keys(v).forEach(function(dk) { node.dataset[dk] = v[dk]; });
      else if (k === 'style') Object.keys(v).forEach(function(sk) { node.style[sk] = v[sk]; });
      else if (k === 'aria') Object.keys(v).forEach(function(ak) { node.setAttribute('aria-' + ak, v[ak]); });
      else node.setAttribute(k, v);
    });
  }
  if (children) {
    (Array.isArray(children) ? children : [children]).forEach(function(c) {
      if (c == null) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
  }
  return node;
}

function txt(str) { return document.createTextNode(str == null ? '' : String(str)); }

function clearEl(node) { while (node.firstChild) node.removeChild(node.firstChild); }

// ---------------------------------------------------------------------------
// Scroll reveal
// ---------------------------------------------------------------------------

// Content is visible by default (configurator.css). The hide-then-animate
// state only applies once <html> has .kc-reveal-ready, which is added here
// when IntersectionObserver exists and the visitor allows motion. Targets
// already revealed are skipped, so repeat calls only pick up new cards.
var revealObserver = null;

function revealAll() {
  document.querySelectorAll('.configurator-reveal:not(.is-visible)').forEach(function(t) {
    t.classList.add('is-visible');
  });
}

// The hidden state is armed on the visitor's first scroll, not on load, so a
// visitor (or crawler, or full-page capture) that never scrolls sees every
// step. Content already on screen at that moment is marked visible first.
var revealArmed = false;
var revealArmListening = false;

function initScrollReveal() {
  if (!revealArmed) {
    if (!revealArmListening) {
      revealArmListening = true;
      window.addEventListener('scroll', function() {
        revealArmed = true;
        initScrollReveal();
      }, { once: true, passive: true });
    }
    return;
  }
  var targets = document.querySelectorAll('.configurator-reveal:not(.is-visible)');
  if (!targets.length) return;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!('IntersectionObserver' in window) || reduceMotion) {
    targets.forEach(function(t) { t.classList.add('is-visible'); });
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    // Failsafe: never leave a step hidden if the observer does not fire.
    if (document.readyState === 'complete') {
      setTimeout(revealAll, 1500);
    } else {
      window.addEventListener('load', function() { setTimeout(revealAll, 1500); }, { once: true });
    }
    setTimeout(revealAll, 3000);
  }
  // Mark targets already in view before the hidden state can apply.
  var vh = window.innerHeight || document.documentElement.clientHeight;
  targets.forEach(function(t) {
    if (t.getBoundingClientRect().top < vh) {
      t.classList.add('is-visible');
    } else {
      revealObserver.observe(t);
    }
  });
  document.documentElement.classList.add('kc-reveal-ready');
}

// ---------------------------------------------------------------------------
// Progress indicator
// ---------------------------------------------------------------------------

function updateMobileProgress(stepNum) {
  var bar = document.querySelector('.configurator-mobile-progress');
  if (!bar) return;
  var textEl = bar.querySelector('.configurator-mobile-progress__text');
  var barEl = bar.querySelector('.configurator-mobile-progress__bar');
  // Count only the steps on the page, so a hidden step does not leave a gap.
  var present = presentSteps();
  // The hero reports step 0: write "Step 0" so the bar hides again.
  var pos = present.indexOf(stepNum) + 1;
  if (!pos && stepNum > 0) return;
  if (textEl) textEl.textContent = 'Step ' + pos + ' of ' + present.length;
  if (barEl) barEl.style.setProperty('--p', String(Math.min(Math.max(pos / present.length, 0), 1)));
}

function presentSteps() {
  var nums = [];
  document.querySelectorAll('[data-configurator-step]').forEach(function(s) {
    var n = parseInt(s.dataset.configuratorStep, 10);
    if (n > 0) nums.push(n);
  });
  return nums.sort(function(a, b) { return a - b; });
}

function initProgressIndicator() {
  var indicator = document.querySelector('.configurator-progress');
  var steps = document.querySelectorAll('[data-configurator-step]');
  if (!steps.length) return;

  // Desktop dots
  if (indicator) {
    // Drop dots for steps that are not on the page (e.g. brand with one brand).
    indicator.querySelectorAll('.configurator-progress__dot').forEach(function(dot) {
      if (!document.querySelector('[data-configurator-step="' + dot.dataset.step + '"]')) dot.remove();
    });
    var dots = indicator.querySelectorAll('.configurator-progress__dot');
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var stepNum = entry.target.dataset.configuratorStep;
          dots.forEach(function(dot) {
            dot.classList.toggle('is-active', dot.dataset.step === stepNum);
          });
          updateMobileProgress(parseInt(stepNum, 10));
        }
      });
    }, { threshold: 0.4 });
    steps.forEach(function(s) { observer.observe(s); });

    dots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        var target = document.querySelector('[data-configurator-step="' + dot.dataset.step + '"]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  } else {
    // Mobile only — still track progress
    var mobileObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          updateMobileProgress(parseInt(entry.target.dataset.configuratorStep, 10));
        }
      });
    }, { threshold: 0.4 });
    steps.forEach(function(s) { mobileObserver.observe(s); });
  }
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function initHeroStep() {
  var btn = document.querySelector('.configurator-hero__cta');
  if (!btn) return;
  btn.addEventListener('click', function(e) {
    e.preventDefault();
    var target = document.querySelector('#step-event') || document.querySelector('[data-configurator-step="1"]');
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  });
}

// ---------------------------------------------------------------------------
// Step 1: Event
// ---------------------------------------------------------------------------

function initEventStep() {
  var grid = document.querySelector('.configurator-event-grid');
  if (!grid || !configData) return;
  clearEl(grid);
  configData.events.forEach(function(evt) {
    grid.appendChild(buildEventCard(evt));
  });
  restoreEventSelection();
}

function buildEventCard(evt) {
  var label = el('span', { class: 'configurator-event-card__label' }, [txt(evt.label)]);
  var desc = el('p', { class: 'configurator-event-card__desc' }, [txt(evt.description || '')]);
  var inner = el('div', { class: 'configurator-event-card__inner' }, [label, desc]);
  var card = el('button', {
    class: 'configurator-event-card configurator-reveal',
    type: 'button',
    dataset: { eventId: evt.id },
    aria: { label: evt.label }
  }, [inner]);
  card.addEventListener('click', function() { selectEvent(evt.id); });
  return card;
}

function selectEvent(eventId) {
  state.event = eventId;
  state.template = null;
  saveState(state);
  document.querySelectorAll('.configurator-event-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.eventId === eventId);
  });
  advanceToStep(2);
  refreshTemplateStep();
}

function restoreEventSelection() {
  if (!state.event) return;
  document.querySelectorAll('.configurator-event-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.eventId === state.event);
  });
}

// ---------------------------------------------------------------------------
// Step 2: Brand
// ---------------------------------------------------------------------------

function initBrandStep() {
  if (!configData) return;
  // One brand: choose it for the shopper. The brand section is disabled in
  // page.build-your-own.json, so the flow skips straight to the design step.
  if (configData.brands.length === 1 && state.brand !== configData.brands[0].id) {
    state.brand = configData.brands[0].id;
    state.tier = configData.brands[0].tier;
    saveState(state);
  }
  var grid = document.querySelector('.configurator-brand-grid');
  if (!grid) return;
  clearEl(grid);
  configData.brands.forEach(function(brand) {
    grid.appendChild(buildBrandCard(brand));
  });
  // The tier legend only means something when there are tiers to compare.
  var legend = document.querySelector('.configurator-brand-legend');
  if (legend) legend.hidden = configData.brands.length < 2;
  restoreBrandSelection();
}

function buildBrandCard(brand) {
  var parts = [];
  if (brand.tier_label) parts.push(el('span', { class: 'configurator-brand-card__tier' }, [txt(brand.tier_label)]));
  parts.push(el('h3', { class: 'configurator-brand-card__name' }, [txt(brand.label)]));
  if (brand.price_range) parts.push(el('p', { class: 'configurator-brand-card__price' }, [txt(brand.price_range)]));
  if (brand.description) parts.push(el('p', { class: 'configurator-brand-card__desc' }, [txt(brand.description)]));
  var card = el('button', {
    class: 'configurator-brand-card configurator-reveal',
    type: 'button',
    dataset: { brandId: brand.id, tier: brand.tier },
    aria: { label: brand.tier_label ? brand.label + ' ' + brand.tier_label : brand.label }
  }, parts);
  card.addEventListener('click', function() { selectBrand(brand.id, brand.tier); });
  return card;
}

function selectBrand(brandId, tier) {
  state.brand = brandId;
  state.tier = tier;
  state.cup = null;
  state.variantId = null;
  saveState(state);
  document.querySelectorAll('.configurator-brand-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.brandId === brandId);
  });
  showFloatingCart();
  advanceToStep(3);
}

function restoreBrandSelection() {
  if (!state.brand) return;
  document.querySelectorAll('.configurator-brand-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.brandId === state.brand);
  });
  if (state.brand) showFloatingCart();
}

// ---------------------------------------------------------------------------
// Step 3: Template
// ---------------------------------------------------------------------------

function initTemplateStep() { refreshTemplateStep(); }

function refreshTemplateStep() {
  var gallery = document.querySelector('.configurator-template-gallery');
  if (!gallery || !configData) return;
  gallery.querySelectorAll('.configurator-template-card:not(.configurator-template-card--upload)').forEach(function(c) { c.remove(); });
  gallery.querySelectorAll('.configurator-template-empty').forEach(function(n) { n.remove(); });

  var templates = [];
  if (state.event) {
    for (var i = 0; i < configData.events.length; i++) {
      if (configData.events[i].id === state.event) { templates = configData.events[i].templates || []; break; }
    }
  } else {
    configData.events.forEach(function(e) { templates = templates.concat(e.templates || []); });
  }

  var uploadCard = gallery.querySelector('.configurator-template-card--upload');

  // Only show templates that have artwork; a grey box with a name is not a design.
  templates = templates.filter(function(t) { return !!t.image; });

  if (!templates.length) {
    var msg = el('p', { class: 'configurator-template-empty' }, [txt(state.event
      ? 'Upload your artwork to continue. Our team checks every file before printing.'
      : 'Pick an occasion above, or upload your artwork.')]);
    if (uploadCard) gallery.insertBefore(msg, uploadCard);
    else gallery.appendChild(msg);
    return;
  }

  templates.forEach(function(tmpl) {
    var card = buildTemplateCard(tmpl);
    if (uploadCard) gallery.insertBefore(card, uploadCard);
    else gallery.appendChild(card);
  });
  restoreTemplateSelection();
  requestAnimationFrame(initScrollReveal);
}

function buildTemplateCard(tmpl) {
  var img = tmpl.image
    ? el('img', { class: 'configurator-template-card__img', src: tmpl.image, alt: tmpl.name })
    : el('div', { class: 'configurator-template-card__placeholder' });
  var name = el('p', { class: 'configurator-template-card__name' }, [txt(tmpl.name)]);
  var card = el('button', {
    class: 'configurator-template-card configurator-reveal',
    type: 'button',
    dataset: { templateId: tmpl.id },
    aria: { label: tmpl.name }
  }, [img, name]);
  card.addEventListener('click', function() { selectTemplate(tmpl.id); });
  return card;
}

function selectTemplate(templateId) {
  state.template = templateId;
  saveState(state);
  document.querySelectorAll('.configurator-template-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.templateId === templateId);
  });
  advanceToStep(4);
}

function restoreTemplateSelection() {
  if (!state.template) return;
  document.querySelectorAll('.configurator-template-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.templateId === state.template);
  });
}

// ---------------------------------------------------------------------------
// Step 4: Cup
// ---------------------------------------------------------------------------

function initCupStep() {
  document.querySelectorAll('.configurator-cup-card').forEach(function(card) {
    card.addEventListener('click', function() {
      selectCup(card.dataset.handle, parseInt(card.dataset.price || '0', 10), card.dataset.variantId);
    });
  });
  restoreCupSelection();
}

function selectCup(handle, price, variantId) {
  state.cup = handle;
  state.price = price;
  state.variantId = variantId || null;
  state.color = null;
  saveState(state);
  document.querySelectorAll('.configurator-cup-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.handle === handle);
  });
  updateFloatingCartPrice();
  advanceToStep(5);
  updateColorSwatches(handle);
}

function restoreCupSelection() {
  if (!state.cup) return;
  document.querySelectorAll('.configurator-cup-card').forEach(function(c) {
    c.classList.toggle('is-selected', c.dataset.handle === state.cup);
  });
}

// ---------------------------------------------------------------------------
// Step 5: Color
// ---------------------------------------------------------------------------

function initColorStep() {
  if (state.cup) updateColorSwatches(state.cup);
}

function updateColorSwatches(productHandle) {
  var swatchWrap = document.querySelector('.configurator-color-swatches');
  if (!swatchWrap) return;
  clearEl(swatchWrap);
  var loading = el('p', { class: 'configurator-color-loading' }, [txt('Loading colors\u2026')]);
  swatchWrap.appendChild(loading);

  var fetchProduct = productCache[productHandle]
    ? Promise.resolve(productCache[productHandle])
    : fetch('/products/' + productHandle + '.js')
        .then(function(r) { if (!r.ok) throw new Error('product fetch failed'); return r.json(); })
        .then(function(d) { productCache[productHandle] = d; return d; });

  fetchProduct
    .then(function(product) {
      clearEl(swatchWrap);
      var colorOption = null;
      var colorIndex = -1;
      product.options.forEach(function(o, i) {
        if (o.toLowerCase() === 'color' || o.toLowerCase() === 'colour') { colorOption = o; colorIndex = i; }
      });

      var seen = {};
      var filtered = product.variants.filter(function(v) {
        var colorVal = colorIndex >= 0 ? v.options[colorIndex] : v.option1;
        if (seen[colorVal]) return false;
        seen[colorVal] = true;
        return true;
      });

      if (!filtered.length) {
        swatchWrap.appendChild(el('p', { class: 'configurator-color-none' }, [txt('No color options available.')]));
        return;
      }

      filtered.forEach(function(variant) {
        var colorName = colorIndex >= 0 ? variant.options[colorIndex] : variant.option1;
        var rawSrc = variant.featured_image && variant.featured_image.src;
        var imgSrc = rawSrc ? rawSrc.replace(/\.([a-z]+)(\?|$)/, '_100x100.$1$2') : null;
        swatchWrap.appendChild(buildColorSwatch(colorName, variant.id, imgSrc));
      });

      restoreColorSelection();
      requestAnimationFrame(initScrollReveal);
    })
    .catch(function() {
      clearEl(swatchWrap);
      swatchWrap.appendChild(el('p', { class: 'configurator-color-none' }, [txt('Colors unavailable \u2014 please contact us.')]));
    });
}

function buildColorSwatch(colorName, variantId, imgSrc) {
  var swatch = el('button', {
    class: 'configurator-color-swatch configurator-reveal',
    type: 'button',
    dataset: { color: colorName, variantId: String(variantId) },
    title: colorName,
    aria: { label: colorName }
  });
  if (imgSrc) {
    var img = el('img', { src: imgSrc, alt: colorName });
    swatch.appendChild(img);
  }
  swatch.appendChild(el('span', { class: 'configurator-color-swatch__label' }, [txt(colorName)]));
  swatch.addEventListener('click', function() { selectColor(colorName, variantId); });
  return swatch;
}

function selectColor(colorName, variantId) {
  state.color = colorName;
  state.variantId = variantId;
  saveState(state);
  document.querySelectorAll('.configurator-color-swatch').forEach(function(s) {
    s.classList.toggle('is-selected', s.dataset.color === colorName);
  });
  var display = document.querySelector('.configurator-color-selected-display');
  if (display) {
    clearEl(display);
    display.appendChild(txt('Selected: '));
    display.appendChild(el('strong', {}, [txt(colorName)]));
  }
  updatePreviewStep();
  updateFloatingCartPrice();
  advanceToStep(6);
}

function restoreColorSelection() {
  if (!state.color) return;
  document.querySelectorAll('.configurator-color-swatch').forEach(function(s) {
    s.classList.toggle('is-selected', s.dataset.color === state.color);
  });
}

// ---------------------------------------------------------------------------
// Step 6: Preview
// ---------------------------------------------------------------------------

function initPreviewStep() {
  updatePreviewStep();
  var qtyInput = document.querySelector('.configurator-preview__qty');
  if (qtyInput) {
    qtyInput.value = state.quantity;
    qtyInput.addEventListener('change', function() {
      var val = parseInt(qtyInput.value, 10);
      if (!isNaN(val) && val >= 1) {
        state.quantity = val;
        saveState(state);
        updatePreviewPrice();
        updateFloatingCartPrice();
        updateCartQuoteToggle(val);
      }
    });
  }
  var cartBtn = document.querySelector('.configurator-preview__add-btn');
  if (cartBtn) cartBtn.addEventListener('click', addToCart);

  var restartBtn = document.querySelector('.configurator-preview__restart');
  if (restartBtn) {
    restartBtn.addEventListener('click', function() {
      sessionStorage.removeItem(STATE_KEY);
      window.location.reload();
    });
  }
}

function updatePreviewStep() {
  updatePreviewSummary();
  updatePreviewPrice();
}

function updatePreviewSummary() {
  var summaryEl = document.querySelector('.configurator-preview__summary');
  if (!summaryEl || !configData) return;
  clearEl(summaryEl);
  var rows = [];
  if (state.event) {
    var ed = null;
    for (var i = 0; i < configData.events.length; i++) { if (configData.events[i].id === state.event) { ed = configData.events[i]; break; } }
    rows.push(['Event', ed ? ed.label : state.event]);
  }
  if (state.brand) {
    var bd = null;
    for (var j = 0; j < configData.brands.length; j++) { if (configData.brands[j].id === state.brand) { bd = configData.brands[j]; break; } }
    rows.push(['Brand', bd ? bd.label : state.brand]);
  }
  if (state.template) rows.push(['Design', state.template === 'custom-upload' ? 'Custom Upload' : state.template]);
  if (state.cup) rows.push(['Cup', state.cup]);
  if (state.color) rows.push(['Color', state.color]);

  if (!rows.length) {
    summaryEl.appendChild(el('p', { style: { fontFamily: 'var(--kc-font-body, Inter, sans-serif)', fontSize: '0.85rem', color: 'rgba(238,238,232,0.4)', margin: '0' } }, [txt('Complete the steps above to see your order summary.')]));
    return;
  }

  var dl = el('dl', { class: 'configurator-preview__dl' });
  rows.forEach(function(row) {
    dl.appendChild(el('dt', { class: 'configurator-preview__dt' }, [txt(row[0])]));
    dl.appendChild(el('dd', { class: 'configurator-preview__dd' }, [txt(row[1])]));
  });
  summaryEl.appendChild(dl);
}

var VOLUME_QUOTE_THRESHOLD = 12;

function updateCartQuoteToggle(qty) {
  var addBtn = document.querySelector('.configurator-preview__add-btn');
  var quoteBtn = document.querySelector('.configurator-preview__quote-btn');
  var volumeNote = document.querySelector('.configurator-preview__volume-note');
  var isVolume = qty >= VOLUME_QUOTE_THRESHOLD;
  if (addBtn) addBtn.hidden = isVolume;
  if (quoteBtn) {
    quoteBtn.hidden = !isVolume;
    if (isVolume) {
      var params = [];
      if (state.cup) params.push('cup=' + encodeURIComponent(state.cup));
      if (state.color) params.push('color=' + encodeURIComponent(state.color));
      if (qty) params.push('qty=' + qty);
      if (state.event) params.push('event=' + encodeURIComponent(state.event));
      if (state.template) params.push('template=' + encodeURIComponent(state.template));
      quoteBtn.href = '/pages/request-a-quote' + (params.length ? '?' + params.join('&') : '');
    }
  }
  if (volumeNote) volumeNote.hidden = !isVolume;
}

function updatePreviewPrice() {
  var priceEl = document.querySelector('.configurator-preview__price');
  if (!priceEl) return;
  clearEl(priceEl);

  var qty = state.quantity || 1;
  updateCartQuoteToggle(qty);

  if (!state.price || !state.variantId) {
    priceEl.appendChild(el('p', { style: { fontFamily: 'var(--kc-font-body, Inter, sans-serif)', fontSize: '0.85rem', color: 'rgba(238,238,232,0.4)', margin: '0' } }, [txt('Select a cup and color to see pricing.')]));
    return;
  }

  var unitPrice = state.price / 100;
  var total = unitPrice * qty;

  priceEl.appendChild(el('div', { class: 'configurator-preview__price-row' }, [
    el('span', {}, [txt('Per unit (' + qty + ' units)')]),
    el('span', {}, [txt('$' + unitPrice.toFixed(2))])
  ]));
  priceEl.appendChild(el('div', { class: 'configurator-preview__price-row configurator-preview__price-row--total' }, [
    el('span', {}, [txt('Order Total')]),
    el('span', {}, [txt('$' + total.toFixed(2))])
  ]));
}

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

function addToCart() {
  var btn = document.querySelector('.configurator-preview__add-btn');
  if (!state.variantId) { showCartError('Please select a cup and color before adding to cart.'); return; }
  if (btn) { btn.disabled = true; btn.textContent = 'Adding\u2026'; }

  var properties = {};
  if (state.event && configData) {
    var ed = null;
    for (var i = 0; i < configData.events.length; i++) { if (configData.events[i].id === state.event) { ed = configData.events[i]; break; } }
    properties['Event'] = ed ? ed.label : state.event;
  }
  if (state.template === 'custom-upload') {
    properties['Design'] = 'Custom Upload';
    if (state.uploadUrl) properties['Upload'] = state.uploadUrl;
  } else if (state.template) {
    properties['Design'] = state.template;
  }
  if (state.color) properties['Color'] = state.color;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: state.variantId, quantity: state.quantity || 1, properties: properties })
  })
  .then(function(r) {
    if (!r.ok) return r.json().then(function(d) { throw new Error(d.description || 'Could not add to cart.'); });
    return r.json();
  })
  .then(function() {
    if (btn) { btn.textContent = 'Added!'; btn.classList.add('is-success'); }
    setTimeout(function() {
      if (btn) { btn.disabled = false; btn.textContent = 'Add to Cart'; btn.classList.remove('is-success'); }
    }, 2500);
    // Horizon uses custom event to open cart drawer
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    // Fallback: click any cart icon/toggle if present
    var toggle = document.querySelector('[data-cart-toggle], .js-cart-toggle, [href="/cart"]');
    if (toggle) toggle.click();
  })
  .catch(function(err) {
    showCartError(err.message || 'Something went wrong. Please try again.');
    if (btn) { btn.disabled = false; btn.textContent = 'Add to Cart'; }
  });
}

function showCartError(msg) {
  var errEl = document.querySelector('.configurator-preview__error');
  if (!errEl) return;
  errEl.textContent = msg;
  errEl.hidden = false;
  setTimeout(function() { errEl.hidden = true; errEl.textContent = ''; }, 5000);
}

// ---------------------------------------------------------------------------
// Floating cart sidebar
// ---------------------------------------------------------------------------

function showFloatingCart() {
  var sidebar = document.querySelector('.configurator-cart-sidebar');
  if (!sidebar) return;
  sidebar.removeAttribute('hidden');
  sidebar.classList.add('is-visible');
  updateFloatingCartPrice();
}

function updateFloatingCartPrice() {
  var sidebar = document.querySelector('.configurator-cart-sidebar');
  if (!sidebar) return;
  var unitEl = sidebar.querySelector('.configurator-cart-sidebar__unit');
  var totalEl = sidebar.querySelector('.configurator-cart-sidebar__total');
  var tierEl = sidebar.querySelector('.configurator-cart-sidebar__tier');

  if (!state.price || !state.variantId) {
    if (unitEl) unitEl.textContent = '\u2014';
    if (totalEl) totalEl.textContent = '\u2014';
    if (tierEl) tierEl.textContent = '';
    return;
  }

  var qty = state.quantity || 1;
  var unitPrice = state.price / 100;
  var total = unitPrice * qty;
  if (unitEl) unitEl.textContent = '$' + unitPrice.toFixed(2) + ' / unit';
  if (totalEl) totalEl.textContent = qty >= VOLUME_QUOTE_THRESHOLD ? 'Quote' : '$' + total.toFixed(2);
  if (tierEl) tierEl.textContent = qty >= VOLUME_QUOTE_THRESHOLD ? 'Volume — get a quote' : '';
  var sidebarQty = sidebar.querySelector('.configurator-cart-sidebar__qty');
  if (sidebarQty && sidebarQty.value != state.quantity) sidebarQty.value = state.quantity;
}

function initFloatingCart() {
  var sidebar = document.querySelector('.configurator-cart-sidebar');
  if (!sidebar) return;
  var qtyInput = sidebar.querySelector('.configurator-cart-sidebar__qty');
  if (qtyInput) {
    qtyInput.value = state.quantity;
    qtyInput.addEventListener('change', function() {
      var val = parseInt(qtyInput.value, 10);
      if (!isNaN(val) && val >= 1) {
        state.quantity = val;
        saveState(state);
        var mainQty = document.querySelector('.configurator-preview__qty');
        if (mainQty) mainQty.value = val;
        updateFloatingCartPrice();
        updatePreviewPrice();
        updateCartQuoteToggle(val);
      }
    });
  }
  // Not state.brand: with one brand it is set on load, before any choice.
  if (state.step >= 2) showFloatingCart();
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

function initUploadCard() {
  var uploadCard = document.querySelector('.configurator-template-card--upload');
  if (!uploadCard) return;
  uploadCard.addEventListener('click', function() {
    var fileInput = document.querySelector('.configurator-upload-input');
    if (fileInput) fileInput.click();
  });
  var fileInput = document.querySelector('.configurator-upload-input');
  if (!fileInput) return;
  fileInput.addEventListener('change', function(e) {
    var file = e.target.files[0];
    if (!file) return;
    state.template = 'custom-upload';
    state.uploadUrl = URL.createObjectURL(file);
    saveState(state);
    document.querySelectorAll('.configurator-template-card').forEach(function(c) {
      c.classList.toggle('is-selected', c.classList.contains('configurator-template-card--upload'));
    });
    var previewImg = uploadCard.querySelector('.configurator-template-card__upload-preview');
    if (!previewImg) {
      previewImg = document.createElement('img');
      previewImg.className = 'configurator-template-card__upload-preview';
      previewImg.alt = 'Your uploaded artwork';
      uploadCard.appendChild(previewImg);
    }
    previewImg.src = state.uploadUrl;
    advanceToStep(4);
  });
}

// ---------------------------------------------------------------------------
// Step advancement
// ---------------------------------------------------------------------------

function advanceToStep(stepNum) {
  state.step = Math.max(state.step, stepNum);
  saveState(state);
  // Skip forward past any step that is not on the page.
  var stepEl = null;
  for (var n = stepNum; n <= 6 && !stepEl; n++) {
    stepEl = document.querySelector('[data-configurator-step="' + n + '"]');
  }
  // Show the running total once the shopper has made a first choice.
  if (stepNum >= 2) showFloatingCart();
  if (stepEl) {
    setTimeout(function() { stepEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 350);
  }
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

function init() {
  fetchConfigData().then(function() {
    initScrollReveal();
    initProgressIndicator();
    initHeroStep();
    initEventStep();
    initBrandStep();
    initTemplateStep();
    initCupStep();
    initColorStep();
    initPreviewStep();
    initFloatingCart();
    initUploadCard();
    requestAnimationFrame(initScrollReveal);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
